import React, { useState, useEffect } from "react";
import { Box, List, CircularProgress, Alert, Skeleton } from "@mui/material";
import EntryListItem from "./EntryListItem";
import EntryDetail from "./EntryDetail";
import { useShowEntries } from "../hooks/useShowEntries";
import { useEntry } from "../hooks/useEntry";

/**
 * A scrollable list component for displaying show entries and entry detail
 * @param {Object} props
 * @param {string} props.showId - The show ID to fetch entries for
 */
export default function EntryList({ showId }) {
  const { entries, loading, error } = useShowEntries(showId);
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  // Automatically select the first entry when entries load
  useEffect(() => {
    if (entries.length > 0 && !selectedEntryId) {
      setSelectedEntryId(entries[0].id);
    }
  }, [entries, selectedEntryId]);

  const {
    entry: selectedEntryDetail,
    loading: entryLoading,
    error: entryError,
  } = useEntry(selectedEntryId);

  if (loading) {
    return (
      <Box
        sx={{
          minWidth: 300,
          width: 300,
          borderRight: "1px solid",
          borderColor: "divider",
          overflowY: "auto",
          p: 2,
        }}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton height={24} />
            <Skeleton height={20} width="80%" />
            <Skeleton height={20} width="60%" />
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minWidth: 300,
          width: 300,
          borderRight: "1px solid",
          borderColor: "divider",
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <Box
        sx={{
          minWidth: 300,
          width: 300,
          borderRight: "1px solid",
          borderColor: "divider",
          overflowY: "auto",
          height: "100%",
        }}
      >
        <List dense>
          {entries.map((entry) => (
            <EntryListItem
              key={entry.id}
              entry={entry}
              selected={selectedEntryId === entry.id}
              onClick={() => setSelectedEntryId(entry.id)}
            />
          ))}
        </List>
      </Box>
      <Box sx={{ flexGrow: 1, height: "100%", overflowY: "auto" }}>
        {selectedEntryId &&
          (entryLoading ? (
            <Box sx={{ pt: "2px", pl: 2 }}>
              <Skeleton height={32} width={200} sx={{ mb: 1 }} />
              <Skeleton height={24} width={150} sx={{ mb: 2 }} />
              <Skeleton height={40} width="100%" sx={{ mb: 2 }} />
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  height={24}
                  width={`${80 - i * 10}%`}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          ) : entryError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{entryError}</Alert>
            </Box>
          ) : (
            selectedEntryDetail && <EntryDetail entry={selectedEntryDetail} />
          ))}
      </Box>
    </Box>
  );
}
